import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import TabsWithPills from "V2.0/common/tabsWithPills";
import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import useWhatsappAssistantMode, {
  WhatsappAssistantMode,
} from "../../Assistant/WhatsappAssistant/useWhatsappAssistantMode";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";
import { putModelSettings } from "../../service";

const extractVoicePrompt = (prompt: string): string => {
  if (!prompt) return "";

  const voiceMatches = prompt.match(/<voice>([\s\S]*?)<\/voice>/g);
  const voiceContent = voiceMatches
    ? voiceMatches
        .map((match) => match.replace(/<\/?voice>/g, "").trim())
        .join("\n\n")
    : "";

  return voiceContent;
};

const extractChatPrompt = (prompt: string): string => {
  if (!prompt) return "";

  const chatMatches = prompt.match(/<chat>([\s\S]*?)<\/chat>/g);
  const chatContent = chatMatches
    ? chatMatches
        .map((match) => match.replace(/<\/?chat>/g, "").trim())
        .join("\n\n")
    : "";

  return chatContent;
};

const extractGeneralPrompt = (prompt) => {
  if (!prompt) return "";

  let generalContent = prompt
    .replace(/<voice>[\s\S]*?<\/voice>/g, "")
    .replace(/<chat>[\s\S]*?<\/chat>/g, "")
    .trim();

  return generalContent;
};

enum PromptStoreAction {
  UPDATE_VOICE_PROMPT,
  UPDATE_CHAT_PROMPT,
  UPDATE_GENERAL_PROMPT,
  RESET_PROMPT,
}

function validatePrompt(state) {
  const {
    chatPrompt,
    voicePrompt,
    generalPrompt,
    maxRawPropmtLength,
    minRawPromptLength,
  } = state;
  const promptLength =
    chatPrompt.length + voicePrompt.length + generalPrompt.length;

  if (promptLength > maxRawPropmtLength) {
    return "Maximum of 30000 characters is allowed";
  }

  if (promptLength < minRawPromptLength) {
    return "Prompt is required";
  }

  return null;
}

function reduceChatPrompt(state, action) {
  const updatedState = {
    ...state,
    chatPrompt: action.payload.chatPrompt,
    isChatPromptUpdated: action.payload.isUpdated,
  };

  const updatedPromptError = validatePrompt(updatedState);

  return {
    ...updatedState,
    errors: {
      rawPrompt: updatedPromptError,
    },
  };
}

function reduceVoicePrompt(state, action) {
  const updatedState = {
    ...state,
    voicePrompt: action.payload.voicePrompt,
    isVoicePromptUpdated: action.payload.isUpdated,
  };

  const updatedPromptError = validatePrompt(updatedState);

  return {
    ...updatedState,
    errors: {
      rawPrompt: updatedPromptError,
    },
  };
}

function reduceGeneralPrompt(state, action) {
  const updatedState = {
    ...state,
    generalPrompt: action.payload.generalPrompt,
    isGeneralPromptUpdated: action.payload.isUpdated,
  };

  const updatedPromptError = validatePrompt(updatedState);

  return {
    ...updatedState,
    errors: {
      rawPrompt: updatedPromptError,
    },
  };
}

function reducePromptStore(state, action) {
  switch (action.type) {
    case PromptStoreAction.UPDATE_CHAT_PROMPT:
      return reduceChatPrompt(state, action);
    case PromptStoreAction.UPDATE_VOICE_PROMPT:
      return reduceVoicePrompt(state, action);
    case PromptStoreAction.UPDATE_GENERAL_PROMPT:
      return reduceGeneralPrompt(state, action);
    case PromptStoreAction.RESET_PROMPT:
      return {
        ...action.payload,
      };
    default:
      return state;
  }
}

const promptStoreInitialState = {
  voicePrompt: "",
  chatPrompt: "",
  generalPrompt: "",
  rawPrompt: "",
  maxRawPromtpLength: 30000,
  minRawPromptLength: 1,
  isVoicePromptUpdated: false,
  isChatPromptUpdated: false,
  isGeneralPromptUpdated: false,
  errors: {
    rawPrompt: null,
  },
};

function getPromptStoreInitialState(rawPrompt) {
  if (!rawPrompt) {
    return promptStoreInitialState;
  }

  return {
    voicePrompt: extractVoicePrompt(rawPrompt),
    chatPrompt: extractChatPrompt(rawPrompt),
    generalPrompt: extractGeneralPrompt(rawPrompt),
    rawPrompt,
    maxRawPromptLength: 30000,
    minRawPromptLength: 1,
    isVoicePromptUpdated: false,
    isChatPromptUpdated: false,
    isGeneralPromptUpdated: false,
    errors: {
      rawPrompt: null,
    },
  };
}

function usePromptStore(rawPrompt) {
  const [state, dispatch] = useReducer(
    reducePromptStore,
    rawPrompt,
    getPromptStoreInitialState
  );

  const updateVoicePrompt = useCallback(function (prompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_VOICE_PROMPT,
      payload: { voicePrompt: prompt, isUpdated: true },
    });
  }, []);

  const updateChatPrompt = useCallback(function (prompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_CHAT_PROMPT,
      payload: { chatPrompt: prompt, isUpdated: true },
    });
  }, []);

  const updateGeneralPrompt = useCallback(function (prompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_GENERAL_PROMPT,
      payload: { generalPrompt: prompt, isUpdated: true },
    });
  }, []);

  const resetVoicePrompt = useCallback(function (rawPrompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_VOICE_PROMPT,
      payload: {
        voicePrompt: extractVoicePrompt(rawPrompt),
        isUpdated: false,
      },
    });
  }, []);

  const resetChatPrompt = useCallback(function (rawPrompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_CHAT_PROMPT,
      payload: {
        chatPrompt: extractChatPrompt(rawPrompt),
        isUpdated: false,
      },
    });
  }, []);

  const resetGeneralPrompt = useCallback(function (rawPrompt) {
    dispatch({
      type: PromptStoreAction.UPDATE_GENERAL_PROMPT,
      payload: {
        generalPrompt: extractGeneralPrompt(rawPrompt),
        isUpdated: false,
      },
    });
  }, []);

  const resetPrompt = useCallback(function (rawPrompt) {
    dispatch({
      type: PromptStoreAction.RESET_PROMPT,
      payload: getPromptStoreInitialState(rawPrompt),
    });
  }, []);

  const value = useMemo(
    function () {
      return {
        state,
        updateChatPrompt,
        updateGeneralPrompt,
        updateVoicePrompt,
        resetPrompt,
        resetChatPrompt,
        resetGeneralPrompt,
        resetVoicePrompt,
      };
    },
    [
      state,
      updateChatPrompt,
      updateGeneralPrompt,
      updateVoicePrompt,
      resetPrompt,
      resetChatPrompt,
      resetGeneralPrompt,
      resetVoicePrompt,
    ]
  );

  return value;
}

function PromptInput(props) {
  const { prompt, errors, onUpdatePrompt } = props;

  return (
    <InputComponent
      type="textArea"
      style={{ fontSize: "14px" }}
      value={prompt}
      error={errors.rawPrompt}
      onChange={(e) => onUpdatePrompt(e.target.value)}
    />
  );
}

function VoicePrompt(props) {
  const { voicePrompt, onUpdateVoicePrompt, errors } = props;

  return (
    <PromptInput
      prompt={voicePrompt}
      onUpdatePrompt={onUpdateVoicePrompt}
      errors={errors}
    />
  );
}

function ChatPrompt(props) {
  const { chatPrompt, onUpdateChatPrompt, errors } = props;

  return (
    <PromptInput
      prompt={chatPrompt}
      onUpdatePrompt={onUpdateChatPrompt}
      errors={errors}
    />
  );
}

function GeneralPrompt(props) {
  const { generalPrompt, onUpdateGeneralPrompt, errors } = props;

  return (
    <PromptInput
      prompt={generalPrompt}
      onUpdatePrompt={onUpdateGeneralPrompt}
      errors={errors}
    />
  );
}

const PromptComponentRegistry = {
  [WhatsappAssistantMode.Voice]: (props) => <VoicePrompt {...props} />,
  [WhatsappAssistantMode.Chat]: (props) => <ChatPrompt {...props} />,
  [WhatsappAssistantMode.General]: (props) => <GeneralPrompt {...props} />,
};

function PromptActions(props) {
  const {
    locked,
    loading,
    disabled,
    onSavePrompt,
    showRevert,
    onRevertPrompt,
  } = props;

  if (locked) {
    return null;
  }

  return (
    <div className="flex shrink-0 gap-x-2">
      <Button
        loading={loading}
        disabled={disabled}
        onClick={onSavePrompt}
      >
        Save Prompt
      </Button>
      {showRevert && (
        <Button
          outline
          onClick={onRevertPrompt}
        >
          Revert
        </Button>
      )}
    </div>
  );
}

function VoiceActions(props) {
  const {
    locked,
    loading,
    isSaveVoicePromptDisabled,
    onSavePrompt,
    showVoicePromptRevert,
    onRevertVoicePrompt,
  } = props;

  return (
    <PromptActions
      locked={locked}
      loading={loading}
      disabled={isSaveVoicePromptDisabled}
      onSavePrompt={onSavePrompt}
      showRevert={showVoicePromptRevert}
      onRevertPrompt={onRevertVoicePrompt}
    />
  );
}

function ChatActions(props) {
  const {
    locked,
    loading,
    isSaveChatPromptDisabled,
    onSavePrompt,
    showChatPromptRevert,
    onRevertChatPrompt,
  } = props;

  return (
    <PromptActions
      locked={locked}
      loading={loading}
      disabled={isSaveChatPromptDisabled}
      onSavePrompt={onSavePrompt}
      showRevert={showChatPromptRevert}
      onRevertPrompt={onRevertChatPrompt}
    />
  );
}

function GeneralActions(props) {
  const {
    locked,
    loading,
    isSaveGeneralPromptDisabled,
    onSavePrompt,
    showGeneralPromptRevert,
    onRevertGeneralPrompt,
  } = props;

  return (
    <PromptActions
      locked={locked}
      loading={loading}
      disabled={isSaveGeneralPromptDisabled}
      onSavePrompt={onSavePrompt}
      showRevert={showGeneralPromptRevert}
      onRevertPrompt={onRevertGeneralPrompt}
    />
  );
}

const PromptActionsRegistry = {
  [WhatsappAssistantMode.Voice]: (props) => <VoiceActions {...props} />,
  [WhatsappAssistantMode.Chat]: (props) => <ChatActions {...props} />,
  [WhatsappAssistantMode.General]: (props) => <GeneralActions {...props} />,
};

export default function WhatsappAssistantPrompt(props) {
  const { modelSettings, syncAssistant, workspaceId, assistantId, locked } =
    props;

  const { activeMode, updateActiveMode, whatsappAssistantModesAsTabs } =
    useWhatsappAssistantMode();

  const {
    state,
    updateVoicePrompt,
    updateChatPrompt,
    updateGeneralPrompt,
    resetChatPrompt,
    resetGeneralPrompt,
    resetVoicePrompt,
    resetPrompt,
  } = usePromptStore(modelSettings?.promptSettings?.rawPrompt);

  const [loading, setLoading] = useState(false);

  useEffect(
    function () {
      if (modelSettings?.promptSettings) {
        resetPrompt(modelSettings.promptSettings.rawPrompt);
      }
    },
    [modelSettings]
  );

  const handleSavePrompt = async function () {
    const voicePrompt = `<voice>\n${state.voicePrompt}\n</voice>`;
    const chatPrompt = `<chat>\n${state.chatPrompt}\n</chat>`;

    const rawPrompt = [state.generalPrompt, voicePrompt, chatPrompt].join("\n");

    const payload = {
      promptSettings: {
        rawPrompt,
      },
    };

    const lifecycleCbs = {
      init: function () {
        setLoading(true);
        message.loading("Saving Prompt");
      },
      success: function () {
        setLoading(false);
        message.destroy();
        message.success("Prompt Saved");
      },
      error: function (e) {
        message.destroy();
        setLoading(false);
        showErrorToast(e, "Failed to save Prompt");
      },
    };

    try {
      lifecycleCbs.init();

      const response = await putModelSettings({
        workspaceId,
        assistantId,
        payload,
      });

      if (response.status === 200) {
        lifecycleCbs.success();
        syncAssistant(function (prevAssistant) {
          return {
            ...prevAssistant,
            modelSettings: response.data,
          };
        });
      }
    } catch (e) {
      lifecycleCbs.error(e);
    }
  };

  return (
    <div className="flex flex-col w-full h-full gap-y-2 px-6 p-4">
      <div>
        <TabsWithPills
          tabs={whatsappAssistantModesAsTabs}
          onTabChange={updateActiveMode}
        />
      </div>
      <div className="flex-1 h-full py-2 pb-6">
        {PromptComponentRegistry[activeMode]({
          ...state,
          onUpdateVoicePrompt: updateVoicePrompt,
          onUpdateChatPrompt: updateChatPrompt,
          onUpdateGeneralPrompt: updateGeneralPrompt,
        })}
      </div>
      {PromptActionsRegistry[activeMode]({
        ...state,
        locked,
        loading,
        disabled: !state.isUpdated || Boolean(state.errors.rawPrompt),
        isSaveVoicePromptDisabled:
          !state.isVoicePromptUpdated || Boolean(state.errors.rawPrompt),
        isSaveChatPromptDisabled:
          !state.isChatPromptUpdated || Boolean(state.errors.rawPrompt),
        isSaveGeneralPromptDisabled:
          !state.isGeneralPromptUpdated || Boolean(state.errors.rawPrompt),
        showVoicePromptRevert: state.isVoicePromptUpdated,
        showChatPromptRevert: state.isChatPromptUpdated,
        showGeneralPromptRevert: state.isGeneralPromptUpdated,
        onSavePrompt: handleSavePrompt,
        onRevertVoicePrompt: () => resetVoicePrompt(state.rawPrompt),
        onRevertChatPrompt: () => resetChatPrompt(state.rawPrompt),
        onRevertGeneralPrompt: () => resetGeneralPrompt(state.rawPrompt),
      })}
    </div>
  );
}
